import moment from 'moment-mini';
import { ColumnSize, DateFilterTypes, ShowIfConfig } from './types';
import { isObject } from '../../../shared/utils/index';

/*
  Originally wanted to use enum here, but enums are not usable
  in Vue2 templates. So using string union types instead.
  Texts are defined in config JSON anyway.
*/
export type FilterType =
  | 'text'
  | 'single_autocomplete'
  | 'multi_autocomplete'
  | 'single_select'
  | 'multi_select'
  | 'date_range'
  | 'number_range';

export type FilterExternalIdsEntity = 'order' | 'cargo';

// used in CustomizableEntityFilter component
export type FilterOptions = ColumnSize & {
  type: FilterType;
  value: string;
  label: string;
  emptyValue: unknown;
  placeholder?: string;
  items?: { text: string; value: any }[];
  emptyCheckboxName?: string;
  maxItemsToShow?: number;
  entityType?: FilterExternalIdsEntity;
  isMain?: boolean;
  splitPosition?: number;
  splitSize?: ColumnSize;
  stepValue?: number;
  showIf?: ShowIfConfig;
};

export function getDefaultFilter(defaultFilters: Record<string, any>) {
  const filterObject: Record<string, any> = {};
  setDefaultFilter(filterObject, defaultFilters);
  return filterObject;
}

export function setDefaultFilter(
  filterObject: Record<string, any>,
  defaultFilters: Record<string, any>,
  valueLimitations?: Record<string, any>
) {
  for (const filterEntry of Object.entries(defaultFilters)) {
    if (
      isObject(filterEntry[1]) &&
      filterEntry[1].filterType === 'date' &&
      !isObject(filterEntry[1].filter)
    ) {
      switch (filterEntry[1].filter) {
        case DateFilterTypes.today: {
          const today = moment().format();
          defaultFilters[filterEntry[0]] = {
            from: today,
            to: today
          };
          break;
        }
        case DateFilterTypes.this_week: {
          const currentWeek = _getCurrentWeek();
          defaultFilters[filterEntry[0]] = {
            from: currentWeek.start,
            to: currentWeek.end
          };
          break;
        }
        case DateFilterTypes.this_month: {
          const currentMonth = _getCurrentMonth();
          defaultFilters[filterEntry[0]] = {
            from: currentMonth.start,
            to: currentMonth.end
          };
          break;
        }
        case DateFilterTypes.this_year: {
          const currentYear = _getCurrentYear();
          defaultFilters[filterEntry[0]] = {
            from: currentYear.start,
            to: currentYear.end
          };
          break;
        }
      }
    }
  }
  Object.assign(filterObject, defaultFilters);

  // optionally apply value limitations for filter keys
  applyValueLimitations(filterObject, valueLimitations);
}

export function applyValueLimitations(
  filterObject: Record<string, any>,
  valueLimitations?: Record<string, any>
) {
  if (!valueLimitations) {
    return;
  }
  for (const [key, value] of Object.entries(valueLimitations ?? [])) {
    if (filterObject[key] !== undefined) {
      // in an array only keep the items that are also in the value limitations
      if (Array.isArray(filterObject[key])) {
        filterObject[key] = filterObject[key].filter((item: any) => value.includes(item));
      }
      // for non-arrays, just set the value from the limitations
      else {
        filterObject[key] = value;
      }
    }
  }
}

function _getCurrentWeek() {
  const start = moment().startOf('isoWeek').toISOString();
  const end = moment().endOf('isoWeek').toISOString();
  return { end, start };
}

function _getCurrentMonth() {
  const start = moment().startOf('month').toISOString();
  const end = moment().endOf('month').toISOString();
  return { end, start };
}

function _getCurrentYear() {
  const start = moment().startOf('year').toISOString();
  const end = moment().endOf('year').toISOString();
  return { end, start };
}

export function getDefaultEmptyValue(filterType: FilterType): unknown {
  switch (filterType) {
    case 'multi_autocomplete':
    case 'multi_select':
      return [];
    case 'date_range':
      return { from: null, to: null };
    default:
      return '';
  }
}
